import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Input, Modal, Form, Select, Typography,
  Space, Alert, Empty, Upload, message, Popconfirm, Tag, Row, Col, Statistic, Segmented,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, UploadOutlined, UserOutlined,
  DeleteOutlined, DownloadOutlined, EditOutlined,
  TeamOutlined, RiseOutlined, UnorderedListOutlined, StopOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;
const { Option } = Select;

const LIST_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ec4899', '#8b5cf6', '#14b8a6'];
const listColor = (id) => LIST_COLORS[(id - 1) % LIST_COLORS.length];

const Subscribers = () => {
  const [subscribers,    setSubscribers]    = useState([]);
  const [total,          setTotal]          = useState(0);
  const [currentPage,    setCurrentPage]    = useState(1);
  const [pageSize]                          = useState(50);
  const [lists,          setLists]          = useState([]);
  const [stats,          setStats]          = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [search,         setSearch]         = useState('');
  const [filterListId,   setFilterListId]   = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const searchDebounceRef = useRef(null);

  // Add
  const [addModal, setAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addForm] = Form.useForm();

  // Edit
  const [editModal, setEditModal] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [editForm] = Form.useForm();

  // Import
  const [importModal,    setImportModal]    = useState(false);
  const [csvFile,        setCsvFile]        = useState(null);
  const [csvRowCount,    setCsvRowCount]    = useState(null);
  const [importListId,   setImportListId]   = useState('');
  const [importListMode, setImportListMode] = useState('existing'); // 'existing' | 'new'
  const [newListName,    setNewListName]    = useState('');
  const [importing,      setImporting]      = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSubscribers = useCallback(async (page = 1, searchVal = search, listId = filterListId) => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: pageSize };
      if (searchVal)  params.search  = searchVal;
      if (listId)     params.list_id = listId;
      const res = await axios.get('/api/subscribers', { headers, params });
      // Handle both paginated { data, total } and legacy array response
      if (Array.isArray(res.data)) {
        setSubscribers(res.data);
        setTotal(res.data.length);
      } else {
        setSubscribers(res.data.data || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, [search, filterListId, pageSize]); // eslint-disable-line

  const fetchLists = useCallback(async () => {
    try {
      const [l, st] = await Promise.all([
        axios.get('/api/lists', { headers }),
        axios.get('/api/subscribers/stats', { headers }),
      ]);
      setLists(l.data);
      setStats(st.data);
    } catch { /* non-fatal */ }
  }, []); // eslint-disable-line

  const fetchAll = useCallback(() => {
    fetchSubscribers(1, search, filterListId);
    fetchLists();
  }, [search, filterListId, fetchSubscribers, fetchLists]);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  // ── Search debounce ───────────────────────────────────────────────────
  const handleSearchChange = (val) => {
    setSearch(val);
    setCurrentPage(1);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchSubscribers(1, val, filterListId);
    }, 350);
  };

  const handleListFilterChange = (v) => {
    const listId = v ?? null;
    setFilterListId(listId);
    setCurrentPage(1);
    fetchSubscribers(1, search, listId);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchSubscribers(page, search, filterListId);
  };

  // ── Add ──────────────────────────────────────────────────────────────
  const handleAdd = async (values) => {
    setSaving(true);
    try {
      await axios.post('/api/subscribers', values, { headers });
      addForm.resetFields();
      setAddModal(false);
      message.success('Subscriber added');
      fetchSubscribers(1, '', null);
      fetchLists();
      setSearch('');
      setFilterListId(null);
      setCurrentPage(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add subscriber');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────
  const openEdit = (record) => {
    setEditingSubscriber(record);
    // Support legacy records that only have 'name'
    let first = record.first_name || '';
    let last  = record.last_name  || '';
    if (!first && !last && record.name) {
      const parts = record.name.trim().split(/\s+/);
      first = parts[0] || '';
      last  = parts.slice(1).join(' ') || '';
    }
    editForm.setFieldsValue({
      first_name: first,
      last_name:  last,
      email:      record.email   || '',
      phone:      record.phone   || '',
      city:       record.city    || '',
      state:      record.state   || '',
      country:    record.country || '',
      tags:       Array.isArray(record.tags) ? record.tags : [],
    });
    setEditModal(true);
  };

  const handleUpdate = async (values) => {
    setUpdating(true);
    try {
      await axios.put(`/api/subscribers/${editingSubscriber.id}`, values, { headers });
      message.success('Subscriber updated');
      setEditModal(false);
      setEditingSubscriber(null);
      editForm.resetFields();
      fetchSubscribers(currentPage, search, filterListId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update subscriber');
    } finally {
      setUpdating(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/subscribers/${id}`, { headers });
      message.success('Subscriber deleted');
      setSelectedRowKeys(prev => prev.filter(k => k !== id));
      fetchSubscribers(currentPage, search, filterListId);
      fetchLists();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedRowKeys.map(id => axios.delete(`/api/subscribers/${id}`, { headers })));
      message.success(`${selectedRowKeys.length} subscriber(s) deleted`);
      setSelectedRowKeys([]);
      fetchSubscribers(currentPage, search, filterListId);
      fetchLists();
    } catch {
      message.error('Some deletions failed');
      fetchSubscribers(currentPage, search, filterListId);
    }
  };


  // ── CSV template download ─────────────────────────────────────────────
  const downloadCsvTemplate = () => {
    const rows = [
      'first_name,last_name,email,phone,city,state,country',
      'John,Doe,john.doe@example.com,+1 555 0001,New York,NY,United States',
      'Jane,Smith,jane.smith@example.com,+1 555 0002,Los Angeles,CA,United States',
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'subscribers_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Import ────────────────────────────────────────────────────────────
  const resetImportModal = () => {
    setCsvFile(null);
    setCsvRowCount(null);
    setImportListId('');
    setNewListName('');
    setImportListMode('existing');
    setImportModal(false);
  };

  const handleCsvSelect = (file) => {
    setCsvFile(file);
    // Count data rows in-browser so user sees subscriber count before importing
    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split('\n').filter(l => l.trim() !== '');
      setCsvRowCount(Math.max(0, lines.length - 1)); // minus header row
    };
    reader.readAsText(file);
    return false; // prevent antd auto-upload
  };

  const handleImport = async () => {
    if (!csvFile) { message.warning('Please select a CSV file'); return; }
    if (importListMode === 'existing' && !importListId) { message.warning('Please select a list'); return; }
    if (importListMode === 'new' && !newListName.trim()) { message.warning('Please enter a name for the new list'); return; }

    setImporting(true);
    try {
      let listId = importListId;

      // Create the new list first if needed
      if (importListMode === 'new') {
        const res = await axios.post('/api/lists', { name: newListName.trim() }, { headers });
        listId = res.data.id;
        message.success(`List "${newListName.trim()}" created`);
      }

      const data = new FormData();
      data.append('file', csvFile);
      data.append('list_id', listId);
      const { data: result } = await axios.post('/api/subscribers/import', data, { headers });
      resetImportModal();

      // Build a human-readable summary
      const parts = [`${result.added} added`];
      if (result.already_in_list > 0) parts.push(`${result.already_in_list} already in list`);
      if (result.skipped_invalid > 0) parts.push(`${result.skipped_invalid} skipped`);
      message.success(`Import complete — ${parts.join(', ')}`, 5);
      fetchSubscribers(1, '', null);
      fetchLists();
      setSearch('');
      setFilterListId(null);
      setCurrentPage(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Subscriber', key: 'subscriber', width: 200, ellipsis: true,
      render: (_, record) => {
        const displayName = [record.first_name, record.last_name].filter(Boolean).join(' ') || record.name || '';
        return (
          <Space size={10}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, #0a40d9, #2e6bf5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Text style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                {(displayName || record.email || '?')[0].toUpperCase()}
              </Text>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{displayName || '—'}</div>
              <div style={{ color: 'var(--text-2)', fontSize: 12 }}>{record.email}</div>
            </div>
          </Space>
        );
      },
    },
    { title: 'Phone', dataIndex: 'phone', render: v => v || '—', responsive: ['md'] },
    {
      title: 'Location', key: 'location', responsive: ['lg'],
      render: (_, r) => [r.city, r.state, r.country].filter(Boolean).join(', ') || '—',
    },
    {
      title: 'Lists', key: 'lists',
      render: (_, record) => (
        <Space size={4} wrap>
          {(record.lists || []).length === 0
            ? <Text style={{ color: 'var(--text-3)', fontSize: 12 }}>—</Text>
            : (record.lists || []).map(l => (
                <span
                  key={l.id}
                  style={{
                    background: listColor(l.id) + '18',
                    color: listColor(l.id),
                    border: `1px solid ${listColor(l.id)}40`,
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 500,
                    padding: '2px 8px',
                    display: 'inline-block',
                  }}
                >
                  {l.name}
                </span>
              ))
          }
        </Space>
      ),
    },
    {
      title: 'Tags', key: 'tags', responsive: ['lg'],
      render: (_, record) => {
        const tags = Array.isArray(record.tags) ? record.tags : [];
        if (!tags.length) return <Text style={{ color: 'var(--text-3)', fontSize: 12 }}>—</Text>;
        return (
          <Space size={4} wrap>
            {tags.map(t => (
              <span
                key={t}
                style={{
                  background: 'var(--surface-2)',
                  color: 'var(--text-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '2px 7px',
                  display: 'inline-block',
                }}
              >
                {t}
              </span>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Added', dataIndex: 'created_at', responsive: ['md'],
      render: d => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-2)' }}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>,
    },
    {
      title: '', align: 'right', width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Button
            icon={<EditOutlined />} size="small" type="text"
            style={{ color: 'var(--brand-primary)' }}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Delete this subscriber?"
            description="This will remove them from all lists."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small" type="text" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Shared subscriber fields ───────────────────────────────────────────
  const SubscriberFields = () => (
    <>
      <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
        <Input placeholder="subscriber@example.com" size="large" style={{ borderRadius: 10 }} />
      </Form.Item>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <Form.Item name="first_name" label="First Name" style={{ marginBottom: 12 }}>
          <Input placeholder="John" style={{ borderRadius: 10 }} />
        </Form.Item>
        <Form.Item name="last_name" label="Last Name" style={{ marginBottom: 12 }}>
          <Input placeholder="Doe" style={{ borderRadius: 10 }} />
        </Form.Item>
        <Form.Item name="phone" label="Phone"><Input placeholder="+1 555 0000" style={{ borderRadius: 10 }} /></Form.Item>
        <Form.Item name="city" label="City"><Input placeholder="New York" style={{ borderRadius: 10 }} /></Form.Item>
        <Form.Item name="state" label="State"><Input placeholder="NY" style={{ borderRadius: 10 }} /></Form.Item>
        <Form.Item name="country" label="Country"><Input placeholder="United States" style={{ borderRadius: 10 }} /></Form.Item>
      </div>
      <Form.Item
        name="tags"
        label={<Space size={4}><span>Tags</span><Text style={{ color: 'var(--text-3)', fontSize: 12, fontWeight: 400 }}>optional</Text></Space>}
        style={{ marginTop: 4 }}
      >
        <Select
          mode="tags"
          placeholder="Type and press Enter to add tags…"
          style={{ width: '100%', borderRadius: 10 }}
          tokenSeparators={[',']}
          maxCount={50}
          open={false}
        />
      </Form.Item>
    </>
  );

  const statCardStyle = {
    borderRadius: 'var(--r-lg)', border: '1px solid var(--border)',
    boxShadow: 'var(--shadow-sm)', height: '100%',
  };

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <Text style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
          <strong style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{total}</strong> subscriber{total !== 1 ? 's' : ''} total
        </Text>
        <Space wrap>
          <Button icon={<UploadOutlined />} onClick={() => setImportModal(true)} style={{ borderRadius: 'var(--r-md)' }}>Import CSV</Button>
          <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setAddModal(true)} style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}>
            Add Subscriber
          </Button>
        </Space>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} />}

      {/* ── Stats Cards ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card style={statCardStyle} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic
              title={<Text style={{ color: 'var(--text-2)', fontSize: 13 }}>Total Subscribers</Text>}
              value={stats?.total ?? '—'}
              prefix={<TeamOutlined style={{ color: 'var(--brand-primary)', marginRight: 4 }} />}
              valueStyle={{ color: 'var(--text)', fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-mono)' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={statCardStyle} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic
              title={<Text style={{ color: 'var(--text-2)', fontSize: 13 }}>New This Month</Text>}
              value={stats?.newThisMonth ?? '—'}
              prefix={<RiseOutlined style={{ color: 'var(--ok)', marginRight: 4 }} />}
              valueStyle={{ color: 'var(--ok)', fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-mono)' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={statCardStyle} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic
              title={<Text style={{ color: 'var(--text-2)', fontSize: 13 }}>Mailing Lists</Text>}
              value={stats?.listCount ?? '—'}
              prefix={<UnorderedListOutlined style={{ color: 'var(--info)', marginRight: 4 }} />}
              valueStyle={{ color: 'var(--text)', fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-mono)' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={statCardStyle} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic
              title={<Text style={{ color: 'var(--text-2)', fontSize: 13 }}>Suppressed</Text>}
              value={stats?.suppressedCount ?? '—'}
              prefix={<StopOutlined style={{ color: 'var(--danger)', marginRight: 4 }} />}
              valueStyle={{ color: stats?.suppressedCount > 0 ? 'var(--danger)' : 'var(--text)', fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-mono)' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Table Card ── */}
      <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search by name or email…"
            prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            style={{ flex: '1 1 180px', minWidth: 160, maxWidth: 280, borderRadius: 'var(--r-md)' }}
            allowClear
            onClear={() => handleSearchChange('')}
          />
          <Select
            placeholder="All Lists"
            allowClear
            value={filterListId}
            onChange={handleListFilterChange}
            style={{ minWidth: 160, borderRadius: 10 }}
          >
            {lists.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
          </Select>
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} subscriber(s)?`}
              description="This cannot be undone."
              onConfirm={handleBulkDelete}
              okText="Delete All" okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />} style={{ borderRadius: 8 }}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
        </div>
        <Table
          dataSource={subscribers}
          columns={columns}
          rowKey="id"
          loading={loading}
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No subscribers yet" /> }}
          pagination={{
            current:   currentPage,
            pageSize,
            total,
            onChange:  handlePageChange,
            showTotal: t => `${t} subscriber${t !== 1 ? 's' : ''}`,
            showSizeChanger: false,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* ── Add Subscriber Modal ── */}
      <Modal
        title={<Space><div style={{ width: 32, height: 32, background: 'var(--ok-bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><UserOutlined style={{ color: 'var(--ok)' }} /></div><span>Add Subscriber</span></Space>}
        open={addModal}
        onCancel={() => { setAddModal(false); addForm.resetFields(); }}
        footer={null} destroyOnHidden width={520}
      >
        <Form form={addForm} onFinish={handleAdd} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="list_id" label="Mailing List" rules={[{ required: true, message: 'Select a list' }]}>
            <Select placeholder="Select a list" size="large">
              {lists.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
            </Select>
          </Form.Item>
          <SubscriberFields />
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setAddModal(false); addForm.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} style={{ borderRadius: 8 }}>Add Subscriber</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit Subscriber Modal ── */}
      <Modal
        title={
          <Space>
            <div style={{ width: 32, height: 32, background: 'var(--brand-tint)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EditOutlined style={{ color: 'var(--brand-primary)' }} />
            </div>
            <span>Edit Subscriber</span>
          </Space>
        }
        open={editModal}
        onCancel={() => { setEditModal(false); setEditingSubscriber(null); editForm.resetFields(); }}
        footer={null} destroyOnHidden width={520}
      >
        {editingSubscriber && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #0a40d9, #2e6bf5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Text style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>
                {([editingSubscriber.first_name, editingSubscriber.last_name].filter(Boolean).join(' ') || editingSubscriber.name || editingSubscriber.email || '?')[0].toUpperCase()}
              </Text>
            </div>
            <div>
              <Text strong style={{ fontSize: 13 }}>{editingSubscriber.name || editingSubscriber.email}</Text>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                {(editingSubscriber.lists || []).map(l => (
                  <span key={l.id} style={{ background: listColor(l.id) + '18', color: listColor(l.id), border: `1px solid ${listColor(l.id)}40`, borderRadius: 20, fontSize: 11, marginRight: 4, padding: '2px 8px', display: 'inline-block' }}>{l.name}</span>
                ))}
              </div>
            </div>
          </div>
        )}
        <Form form={editForm} onFinish={handleUpdate} layout="vertical">
          <SubscriberFields />
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setEditModal(false); setEditingSubscriber(null); editForm.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={updating} style={{ borderRadius: 'var(--r-md)' }}>
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Import CSV Modal ── */}
      <Modal
        title={<Space><div style={{ width: 32, height: 32, background: 'var(--info-bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><UploadOutlined style={{ color: 'var(--info)' }} /></div><span>Import from CSV</span></Space>}
        open={importModal}
        onCancel={resetImportModal}
        onOk={handleImport}
        okText="Import"
        okButtonProps={{ loading: importing, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        destroyOnHidden width={480}
      >
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* List selector */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Mailing List</Text>
            <Segmented
              block
              value={importListMode}
              onChange={v => { setImportListMode(v); setImportListId(''); setNewListName(''); }}
              options={[
                { label: 'Select existing list', value: 'existing' },
                { label: 'Create new list', value: 'new' },
              ]}
              style={{ marginBottom: 10 }}
            />
            {importListMode === 'existing' ? (
              <Select
                placeholder="Choose a list…"
                value={importListId || undefined}
                onChange={setImportListId}
                style={{ width: '100%' }}
                size="large"
                showSearch
                optionFilterProp="children"
              >
                {lists.map(l => (
                  <Option key={l.id} value={l.id}>
                    <Space>
                      <UnorderedListOutlined style={{ color: 'var(--brand-primary)' }} />
                      {l.name}
                      {l.subscriber_count !== undefined && (
                        <Text style={{ color: 'var(--text-3)', fontSize: 12 }}>({l.subscriber_count})</Text>
                      )}
                    </Space>
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                placeholder="e.g., Product Launch List"
                value={newListName}
                onChange={e => setNewListName(e.target.value)}
                size="large"
                style={{ borderRadius: 10 }}
                prefix={<PlusOutlined style={{ color: 'var(--brand-primary)' }} />}
                autoFocus
              />
            )}
          </div>

          {/* File upload */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text strong>CSV File</Text>
              {csvFile && csvRowCount !== null && (
                <span style={{ background: 'var(--ok-bg)', color: 'var(--ok)', border: '1px solid var(--ok)', borderRadius: 20, fontSize: 11, fontWeight: 600, padding: '2px 10px', fontFamily: 'var(--font-mono)' }}>
                  {csvRowCount} subscriber{csvRowCount !== 1 ? 's' : ''} detected
                </span>
              )}
            </div>
            <Upload
              accept=".csv"
              beforeUpload={handleCsvSelect}
              onRemove={() => { setCsvFile(null); setCsvRowCount(null); }}
              maxCount={1}
              fileList={csvFile ? [{ uid: '1', name: csvFile.name, status: 'done' }] : []}
            >
              <Button icon={<UploadOutlined />} style={{ borderRadius: 10, width: '100%' }} size="large">
                Select CSV file
              </Button>
            </Upload>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'var(--info-bg)', border: '1px solid var(--info)',
            borderRadius: 'var(--r-md)', padding: '10px 14px', gap: 12,
          }}>
            <div style={{ fontSize: 12, color: 'var(--info)', lineHeight: 1.5, fontFamily: 'var(--font-mono)' }}>
              <div style={{ fontWeight: 600, marginBottom: 2, fontFamily: 'inherit' }}>Required columns</div>
              <div>first_name, last_name, <strong>email</strong>, phone, city, state, country</div>
            </div>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={downloadCsvTemplate}
              style={{ borderRadius: 'var(--r-md)', borderColor: 'var(--info)', color: 'var(--info)', background: 'var(--surface)', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Download template
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Subscribers;
